class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      // Use event.state if available, otherwise read current URL params
      // This prevents resetting filters when overlay close/open triggers popstate with null state
      const searchParams = event.state?.searchParams ?? window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        // FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    // FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    if (typeof formatGridItems === 'function') formatGridItems();

    const urlParams = new URLSearchParams(window.location.search);
    const gridSize = urlParams.get('grid_size');
    const grid = document.getElementById('ProductGridContainer').querySelector('.js-product-grid');

    if (gridSize == 2 || gridSize == 1) {
      grid.setAttribute('data-grid-size-mobile', gridSize);
    }
    if (gridSize == 5 || gridSize == 3) {
      grid.setAttribute('data-grid-size-desktop', gridSize);
    }

    // Dispatch custom event to notify that facets have been updated
    document.dispatchEvent(
      new CustomEvent('facets:updated', {
        detail: { html: html },
      })
    );
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        // Preserve open state of accordion details elements before replacing
        const openDetailsIds = Array.from(currentElement.querySelectorAll('details[open]')).map((el) => el.id);

        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;

        // Restore open state of accordions
        openDetailsIds.forEach((id) => {
          const detailsElement = document.getElementById(id);
          if (detailsElement) detailsElement.setAttribute('open', '');
        });
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetFiltersForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Size filter "Show only in stock" toggle
(function initSizeStockToggle() {
  const PARAM_NAME = 'show_in_stock';
  // Track state separately since facets URL updates may remove our param
  let isToggleChecked = new URL(window.location.href).searchParams.get(PARAM_NAME) === '1';

  function updateUrlParam(checked) {
    const url = new URL(window.location.href);
    if (checked) {
      url.searchParams.set(PARAM_NAME, '1');
    } else {
      url.searchParams.delete(PARAM_NAME);
    }
    history.replaceState(history.state, '', url.toString());
  }

  function applyVisualState(toggle, sizeFilter, checked) {
    toggle.checked = checked;
    if (checked) {
      sizeFilter.setAttribute('data-hide-out-of-stock', '');
    } else {
      sizeFilter.removeAttribute('data-hide-out-of-stock');
    }
  }

  function resetToggle() {
    isToggleChecked = false;
    updateUrlParam(false);
    document.querySelectorAll('[data-show-only-in-stock]').forEach((toggle) => {
      const sizeFilter = toggle.closest('[data-size-filter]');
      if (sizeFilter) {
        applyVisualState(toggle, sizeFilter, false);
      }
    });
  }

  function setupToggle(toggle) {
    const sizeFilter = toggle.closest('[data-size-filter]');
    if (!sizeFilter) return;

    // Apply current state
    applyVisualState(toggle, sizeFilter, isToggleChecked);

    toggle.addEventListener('change', function () {
      isToggleChecked = this.checked;
      updateUrlParam(this.checked);
      if (this.checked) {
        sizeFilter.setAttribute('data-hide-out-of-stock', '');
      } else {
        sizeFilter.removeAttribute('data-hide-out-of-stock');
      }
    });
  }

  function setupClearLinks() {
    // Reset toggle when clear/remove filter links are clicked
    document.querySelectorAll('[data-facets-clear], .js-facet-remove').forEach((link) => {
      link.addEventListener('click', resetToggle);
    });
  }

  // Initial setup
  document.querySelectorAll('[data-show-only-in-stock]').forEach(setupToggle);
  setupClearLinks();

  // Re-setup after facets are updated (when filters change)
  document.addEventListener('facets:updated', function () {
    // Re-add URL param if it was removed by facets URL update
    if (isToggleChecked) {
      updateUrlParam(true);
    }

    document.querySelectorAll('[data-show-only-in-stock]').forEach((toggle) => {
      const sizeFilter = toggle.closest('[data-size-filter]');
      if (!sizeFilter) return;

      applyVisualState(toggle, sizeFilter, isToggleChecked);

      // Re-attach event listener (since DOM was replaced)
      toggle.addEventListener('change', function () {
        isToggleChecked = this.checked;
        updateUrlParam(this.checked);
        if (this.checked) {
          sizeFilter.setAttribute('data-hide-out-of-stock', '');
        } else {
          sizeFilter.removeAttribute('data-hide-out-of-stock');
        }
      });
    });

    // Re-setup clear links (since DOM was replaced)
    setupClearLinks();
  });
})();

// Mobile facets footer state management
(function initFacetsFooter() {
  // Track if sort_by has changed from initial value
  let initialSortBy = null;

  function updateFooterState(form) {
    if (!form) return;

    const footer = form.querySelector('[data-facets-footer]');
    if (!footer) return;

    const applyButton = footer.querySelector('[data-facets-apply-button]');
    const clearLink = footer.querySelector('[data-facets-clear]');

    // Count checked filter checkboxes (excluding sort_by radios and stock toggle)
    const checkedFilters = form.querySelectorAll(
      'input[type="checkbox"]:checked:not([data-show-only-in-stock]), input[type="radio"][name^="filter"]:checked'
    ).length;

    // Check if sort_by has changed
    const currentSortBy = form.querySelector('input[name="sort_by"]:checked, input[name="sort_by"][type="hidden"]');
    const sortByChanged = currentSortBy && initialSortBy !== null && currentSortBy.value !== initialSortBy;

    const hasActiveSelection = checkedFilters > 0 || sortByChanged;

    if (applyButton) {
      if (hasActiveSelection) {
        applyButton.classList.remove('button--disabled');
        applyButton.disabled = false;
      } else {
        applyButton.classList.add('button--disabled');
        applyButton.disabled = true;
      }
    }

    if (clearLink) {
      clearLink.classList.toggle('hidden', !hasActiveSelection);
    }
  }

  function captureInitialSortBy(form) {
    if (!form) return;
    const sortByInput = form.querySelector('input[name="sort_by"]:checked, input[name="sort_by"][type="hidden"]');
    if (sortByInput) {
      initialSortBy = sortByInput.value;
    }
  }

  function setupForm(form) {
    if (!form) return;

    // Capture initial sort_by value
    captureInitialSortBy(form);

    // Update on any input change within the form
    form.addEventListener('change', function (e) {
      // Ignore the stock toggle
      if (e.target.hasAttribute('data-show-only-in-stock')) return;
      updateFooterState(form);
    });

    // Initial state
    updateFooterState(form);
  }

  // Setup mobile facets form
  const mobileForm = document.getElementById('FacetFiltersFormMobile');
  setupForm(mobileForm);

  // Re-setup after facets are updated (don't recapture initialSortBy - keep tracking against page load value)
  document.addEventListener('facets:updated', function () {
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    updateFooterState(mobileForm);
  });
})();
